import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, auth, inputCls, Field, Modal, PrimaryBtn, errMsg, fmtDate } from './finance/shared';
import { EmptyState } from '../../components/dashboard/TodaySchedule';
import Icon from '../../components/dashboard/DashboardIcons';

const STATUS_LABELS = { upcoming: 'قادم', active: 'نشط', closed: 'مغلق' };
const STATUS_CLS = { upcoming: 'bg-slate-100 text-slate-600', active: 'bg-teal-50 text-teal-700', closed: 'bg-slate-100 text-slate-400' };

const MigrationWizard = ({ season, categories, onClose, notify, onDone }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [decisions, setDecisions] = useState({}); // player_id -> {apply, target_category_id}
  const [filter, setFilter] = useState('needs_change');
  const [confirmApply, setConfirmApply] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState(null);
  const [candidates, setCandidates] = useState(null); // {categories, teams, employees}
  const [rosterSel, setRosterSel] = useState({ category_ids: [], team_ids: [], employee_ids: [] });

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const [prevRes, candRes] = await Promise.all([
          axios.post(`${API}/migrations/season-preview`, { season_id: season.id }, auth()),
          axios.get(`${API}/seasons/${season.id}/roster-candidates`, auth()).catch(() => null),
        ]);
        if (!active) return;
        setData(prevRes.data);
        const initial = {};
        (prevRes.data.items || []).forEach((it) => {
          if (it.needs_change) initial[it.player_id] = { apply: true, target_category_id: it.suggested_category_id };
        });
        setDecisions(initial);
        if (candRes?.data) {
          setCandidates(candRes.data);
          setRosterSel({
            category_ids: (candRes.data.categories || []).filter((c) => !c.already_linked).map((c) => c.id),
            team_ids: (candRes.data.teams || []).filter((t) => !t.already_linked).map((t) => t.id),
            employee_ids: (candRes.data.employees || []).filter((e) => !e.already_linked).map((e) => e.id),
          });
        }
      } catch (err) { setError(errMsg(err, 'فشل تحميل المعاينة')); }
      setLoading(false);
    })();
    return () => { active = false; };
  }, [season.id]);

  const items = data?.items || [];
  const visible = items.filter((it) => {
    if (filter === 'needs_change') return it.needs_change;
    if (filter === 'issues') return Boolean(it.reason);
    return true;
  });

  const toggle = (playerId, defaultCat) => setDecisions((d) => {
    const cur = d[playerId];
    if (cur?.apply) { const n = { ...d }; delete n[playerId]; return n; }
    return { ...d, [playerId]: { apply: true, target_category_id: defaultCat } };
  });
  const setTarget = (playerId, catId) => setDecisions((d) => ({ ...d, [playerId]: { apply: true, target_category_id: catId } }));
  const selectedCount = Object.values(decisions).filter((d) => d.apply).length;

  const toggleRoster = (kind, id) => setRosterSel((s) => {
    const list = s[kind];
    return { ...s, [kind]: list.includes(id) ? list.filter((x) => x !== id) : [...list, id] };
  });
  const rosterCount = rosterSel.category_ids.length + rosterSel.team_ids.length + rosterSel.employee_ids.length;

  const submitApply = async () => {
    setApplying(true);
    const payload = {
      season_id: season.id,
      decisions: Object.entries(decisions).filter(([, v]) => v.apply).map(([player_id, v]) => ({ player_id, apply: true, target_category_id: v.target_category_id || null })),
    };
    try {
      let playersAdded = 0;
      if (payload.decisions.length) {
        const res = await axios.post(`${API}/migrations/season-apply`, payload, auth());
        playersAdded = res.data.roster_player_ids?.length || 0;
      }
      let rosterAdded = { categories: 0, teams: 0, employees: 0 };
      if (rosterCount > 0) {
        const r = await axios.post(`${API}/seasons/${season.id}/roster/add`, rosterSel, auth());
        rosterAdded = r.data.added || rosterAdded;
      }
      notify(`تمت الإضافة لروستر موسم ${season.name}: ${playersAdded} لاعب، ${rosterAdded.categories} فئة، ${rosterAdded.teams} فريق، ${rosterAdded.employees} موظف`);
      setConfirmApply(false);
      onDone();
    } catch (err) { setError(errMsg(err, 'فشل تنفيذ الترحيل')); setConfirmApply(false); }
    setApplying(false);
  };

  const totalToConfirm = selectedCount + rosterCount;

  return (
    <Modal open title={`معالج بدء الموسم — ${season.name}`} onClose={onClose} wide>
      {error && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="migration-error">{error}</div>}
      {loading ? <p className="py-6 text-center text-sm text-slate-400">جارٍ تحميل المعاينة...</p> : !data ? null : (
        <div className="space-y-4" data-testid="migration-wizard">
          <p className="text-xs text-slate-500">التاريخ المرجعي لحساب العمر: <b>{fmtDate(data.age_reference_date)}</b> — هذه معاينة فقط ولن يتغيّر أي شيء إلا بعد الضغط على «تنفيذ الترحيل». كل لاعب/فئة/فريق/موظف تختاره يُضاف لروستر هذا الموسم بمعزل كامل عن ارتباطه بأي موسم سابق (لا يُلمس).</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <div className="rounded-lg bg-slate-50 p-2 text-center"><div className="text-lg font-bold text-slate-900">{data.summary.total_players}</div><div className="text-[11px] text-slate-500">إجمالي اللاعبين</div></div>
            <div className="rounded-lg bg-amber-50 p-2 text-center"><div className="text-lg font-bold text-amber-700">{data.summary.needs_change}</div><div className="text-[11px] text-amber-700">يحتاج تغيير فئة</div></div>
            <div className="rounded-lg bg-red-50 p-2 text-center"><div className="text-lg font-bold text-red-600">{data.summary.no_match}</div><div className="text-[11px] text-red-600">بلا فئة مطابقة</div></div>
            <div className="rounded-lg bg-slate-50 p-2 text-center"><div className="text-lg font-bold text-slate-500">{data.summary.missing_birth_date}</div><div className="text-[11px] text-slate-500">بلا تاريخ ميلاد</div></div>
          </div>

          <div>
            <div className="mb-1.5 text-xs font-bold text-slate-600">اللاعبون المرشحون للترحيل لهذا الموسم</div>
            <div className="flex gap-1 rounded-lg border border-slate-200 bg-white p-1" data-testid="migration-filter-toggle">
              {[['needs_change', 'يحتاج تغيير'], ['issues', 'مشاكل'], ['all', 'الكل']].map(([k, l]) => (
                <button key={k} onClick={() => setFilter(k)} className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition-colors ${filter === k ? 'bg-[#16233b] text-white' : 'text-slate-600 hover:bg-slate-50'}`} data-testid={`migration-filter-${k}`}>{l}</button>
              ))}
            </div>

            <div className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-slate-100">
              {visible.length === 0 ? <div className="p-6"><EmptyState icon="check" text="لا توجد عناصر في هذا الفلتر" testId="migration-empty" /></div> : visible.map((it) => (
                <div key={it.player_id} className="flex flex-wrap items-center gap-2 border-b border-slate-50 px-3 py-2 text-sm" data-testid="migration-row">
                  {it.reason ? <span className="w-4 shrink-0" /> : (
                    <input type="checkbox" checked={Boolean(decisions[it.player_id]?.apply)} onChange={() => toggle(it.player_id, it.suggested_category_id)} data-testid="migration-row-checkbox" />
                  )}
                  <span className="min-w-[140px] flex-1 truncate font-semibold text-slate-800">{it.player_name}</span>
                  <span className="text-xs text-slate-500">العمر: {it.computed_age ?? '—'}</span>
                  <span className="text-xs text-slate-400">الحالية: {it.current_category_name}</span>
                  <Icon name="arrowLeft" className="w-3.5 h-3.5 text-slate-300" />
                  {it.reason ? (
                    <span className="rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-red-600" data-testid="migration-reason-badge">
                      {it.reason === 'missing_birth_date' ? 'بلا تاريخ ميلاد' : 'بلا فئة مطابقة'}
                    </span>
                  ) : (
                    <select value={decisions[it.player_id]?.target_category_id || it.suggested_category_id || ''} onChange={(e) => setTarget(it.player_id, e.target.value)} className={`${inputCls} !w-40 !py-1`} data-testid="migration-target-select">
                      {categories.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  )}
                </div>
              ))}
            </div>
          </div>

          {candidates && (candidates.categories.length > 0 || candidates.teams.length > 0 || candidates.employees.length > 0) && (
            <div>
              <div className="mb-1.5 text-xs font-bold text-slate-600">الفئات والفرق والموظفون النشطون — أضفهم لروستر هذا الموسم</div>
              <div className="grid gap-3 sm:grid-cols-3" data-testid="roster-candidates-section">
                {[['category_ids', 'categories', 'الفئات'], ['team_ids', 'teams', 'الفرق'], ['employee_ids', 'employees', 'الموظفون']].map(([selKey, dataKey, label]) => (
                  <div key={selKey} className="rounded-lg border border-slate-100 p-2">
                    <div className="mb-1 text-[11px] font-bold text-slate-500">{label} ({rosterSel[selKey].length}/{candidates[dataKey].length})</div>
                    <div className="max-h-32 space-y-1 overflow-y-auto">
                      {candidates[dataKey].map((c) => (
                        <label key={c.id} className="flex items-center gap-2 text-xs text-slate-700" data-testid="roster-candidate-row">
                          <input type="checkbox" checked={rosterSel[selKey].includes(c.id)} onChange={() => toggleRoster(selKey, c.id)} data-testid="roster-candidate-checkbox" />
                          <span className="truncate">{c.name || c.full_name}</span>
                          {c.already_linked && <span className="rounded-full bg-teal-50 px-1.5 py-0.5 text-[10px] text-teal-700">مرتبط مسبقاً</span>}
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-slate-100 pt-3">
            <span className="text-xs text-slate-500">محدَّد للإضافة لهذا الموسم: <b data-testid="migration-selected-count">{totalToConfirm}</b> عنصر</span>
            <div className="flex gap-2">
              <button onClick={onClose} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إغلاق</button>
              <PrimaryBtn onClick={() => setConfirmApply(true)} disabled={totalToConfirm === 0} data-testid="migration-apply-btn">تنفيذ الترحيل ({totalToConfirm})</PrimaryBtn>
            </div>
          </div>
        </div>
      )}

      <Modal open={confirmApply} title="تأكيد تنفيذ الترحيل" onClose={() => setConfirmApply(false)}>
        <p className="text-sm text-slate-600">سيُضاف <b>{selectedCount}</b> لاعباً و<b>{rosterCount}</b> عنصر (فئة/فريق/موظف) لروستر موسم <b>{season.name}</b> فوراً، دون أي تأثير على المواسم السابقة. هذا الإجراء يُسجَّل بسجل تدقيق ويمكن التراجع عنه. متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setConfirmApply(false)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">تراجع</button>
          <button onClick={submitApply} disabled={applying} className="rounded-lg bg-[#16233b] px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="migration-confirm-apply-btn">{applying ? 'جارٍ التنفيذ...' : 'تأكيد وتنفيذ'}</button>
        </div>
      </Modal>
    </Modal>
  );
};

export default function SeasonsSection({ notify }) {
  const [seasons, setSeasons] = useState([]);
  const [categories, setCategories] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [seasonModal, setSeasonModal] = useState(false);
  const [seasonForm, setSeasonForm] = useState({});
  const [seasonError, setSeasonError] = useState(null);
  const [wizardSeason, setWizardSeason] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [undoTarget, setUndoTarget] = useState(null);
  const [editingSeason, setEditingSeason] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [s, c, m] = await Promise.allSettled([axios.get(`${API}/seasons`, auth()), axios.get(`${API}/categories`, auth()), axios.get(`${API}/migrations`, auth())]);
    if (s.status === 'fulfilled') setSeasons(s.value.data || []);
    if (c.status === 'fulfilled') setCategories(c.value.data || []);
    if (m.status === 'fulfilled') setMigrations(m.value.data || []);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openSeasonForm = () => { setEditingSeason(null); setSeasonForm({ name: '', start_date: '', end_date: '', age_reference_date: '' }); setSeasonError(null); setSeasonModal(true); };
  const openEditForm = (s) => { setEditingSeason(s); setSeasonForm({ name: s.name, start_date: s.start_date, end_date: s.end_date, age_reference_date: s.age_reference_date || '' }); setSeasonError(null); setSeasonModal(true); };
  const submitSeason = async (e) => {
    e.preventDefault();
    if (!seasonForm.name?.trim() || !seasonForm.start_date || !seasonForm.end_date) { setSeasonError('الاسم وتاريخا البداية والنهاية مطلوبة'); return; }
    if (seasonForm.end_date < seasonForm.start_date) { setSeasonError('تاريخ نهاية الموسم يجب أن يكون بعد تاريخ البداية'); return; }
    try {
      const payload = { name: seasonForm.name.trim(), start_date: seasonForm.start_date, end_date: seasonForm.end_date, age_reference_date: seasonForm.age_reference_date || null };
      if (editingSeason) {
        await axios.put(`${API}/seasons/${editingSeason.id}`, payload, auth());
        notify(`تم تعديل موسم ${payload.name}`);
      } else {
        await axios.post(`${API}/seasons`, payload, auth());
        notify('تم إنشاء الموسم');
      }
      setSeasonModal(false); setEditingSeason(null); load();
      window.dispatchEvent(new Event('seasons-updated'));
    } catch (err) { setSeasonError(errMsg(err, 'فشل الحفظ')); }
  };
  const activate = async (s) => {
    try {
      await axios.post(`${API}/seasons/${s.id}/activate`, {}, auth()); notify(`تم تفعيل موسم ${s.name}`); load();
      window.dispatchEvent(new Event('seasons-updated'));
    }
    catch (err) { notify(errMsg(err, 'فشل التفعيل')); }
  };
  const removeSeason = async (s) => {
    try { await axios.delete(`${API}/seasons/${s.id}`, auth()); notify('تم حذف الموسم'); load(); window.dispatchEvent(new Event('seasons-updated')); }
    catch (err) { notify(errMsg(err, 'تعذر الحذف')); }
  };
  const undo = async () => {
    try {
      const res = await axios.post(`${API}/migrations/${undoTarget.id}/undo`, {}, auth());
      notify(`تم التراجع — أُرجع ${res.data.reverted_count} لاعباً${res.data.skipped?.length ? ` (تعذّر التراجع عن ${res.data.skipped.length})` : ''}`);
      setUndoTarget(null); load();
    } catch (err) { notify(errMsg(err, 'فشل التراجع')); setUndoTarget(null); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white" data-testid="seasons-section">
      <div className="flex items-center justify-between border-b border-slate-100 px-4 py-3">
        <div>
          <div className="text-sm font-bold text-slate-800">المواسم والترحيل الموسمي</div>
          <div className="text-xs text-slate-500">أضف موسماً، عايِن اقتراحات ترحيل الفئات حسب العمر، ونفّذ فقط ما تعتمده</div>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setHistoryOpen(true)} className="rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-700 hover:border-teal-300" data-testid="migration-history-btn">سجل الترحيلات</button>
          <PrimaryBtn onClick={openSeasonForm} data-testid="season-add-btn"><Icon name="plus" className="w-4 h-4" /> إضافة موسم</PrimaryBtn>
        </div>
      </div>

      {loading ? <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">{[1, 2, 3].map((i) => <div key={i} className="h-28 animate-pulse rounded-lg bg-slate-100" />)}</div>
        : seasons.length === 0 ? <div className="p-6"><EmptyState icon="calendar" text="لا توجد مواسم بعد — أضف أول موسم" testId="seasons-empty" /></div> : (
          <div className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {seasons.map((s) => (
              <div key={s.id} className="rounded-xl border border-slate-100 bg-slate-50/50 p-4" data-testid="season-card">
                <div className="flex items-start justify-between">
                  <div>
                    <div className="text-sm font-bold text-slate-900">{s.name}</div>
                    <div className="mt-0.5 text-xs text-slate-500">{fmtDate(s.start_date)} — {fmtDate(s.end_date)}</div>
                  </div>
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${STATUS_CLS[s.status]}`} data-testid="season-status-badge">{STATUS_LABELS[s.status]}</span>
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  {s.status !== 'active' && <button onClick={() => activate(s)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-teal-300" data-testid="season-activate-btn">تفعيل</button>}
                  <button onClick={() => setWizardSeason(s)} className="rounded-lg bg-[#16233b] px-2.5 py-1 text-xs font-semibold text-white" data-testid="season-preview-migration-btn">معاينة الترحيل</button>
                  <button onClick={() => openEditForm(s)} className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-semibold text-slate-600 hover:border-teal-300" data-testid="season-edit-btn">تعديل</button>
                  <button onClick={() => removeSeason(s)} className="rounded-lg p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" data-testid="season-del-btn"><Icon name="trash" className="w-4 h-4" /></button>
                </div>
              </div>
            ))}
          </div>
        )}

      <Modal open={seasonModal} title={editingSeason ? `تعديل موسم — ${editingSeason.name}` : 'إضافة موسم'} onClose={() => { setSeasonModal(false); setEditingSeason(null); }}>
        {seasonError && <div className="mb-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="season-form-error">{seasonError}</div>}
        <form onSubmit={submitSeason} className="space-y-3">
          <Field label="اسم الموسم" required><input className={inputCls} placeholder="مثال: 2026-2027" value={seasonForm.name || ''} onChange={(e) => setSeasonForm({ ...seasonForm, name: e.target.value })} data-testid="season-input-name" /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="تاريخ البداية" required><input type="date" className={inputCls} value={seasonForm.start_date || ''} onChange={(e) => setSeasonForm({ ...seasonForm, start_date: e.target.value })} data-testid="season-input-start" /></Field>
            <Field label="تاريخ النهاية" required><input type="date" className={inputCls} value={seasonForm.end_date || ''} onChange={(e) => setSeasonForm({ ...seasonForm, end_date: e.target.value })} data-testid="season-input-end" /></Field>
          </div>
          <Field label="التاريخ المرجعي لحساب العمر (اختياري — افتراضياً تاريخ البداية)"><input type="date" className={inputCls} value={seasonForm.age_reference_date || ''} onChange={(e) => setSeasonForm({ ...seasonForm, age_reference_date: e.target.value })} data-testid="season-input-ref-date" /></Field>
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => { setSeasonModal(false); setEditingSeason(null); }} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
            <PrimaryBtn type="submit" data-testid="season-form-submit">{editingSeason ? 'حفظ التعديل' : 'حفظ'}</PrimaryBtn>
          </div>
        </form>
      </Modal>

      {wizardSeason && <MigrationWizard season={wizardSeason} categories={categories} onClose={() => setWizardSeason(null)} notify={notify} onDone={() => { setWizardSeason(null); load(); }} />}

      <Modal open={historyOpen} title="سجل الترحيلات الموسمية" onClose={() => setHistoryOpen(false)} wide>
        {migrations.length === 0 ? <div className="p-6"><EmptyState icon="clock" text="لا يوجد سجل ترحيلات بعد" testId="migrations-history-empty" /></div> : (
          <div className="max-h-96 space-y-2 overflow-y-auto">
            {migrations.map((m) => (
              <div key={m.id} className="rounded-lg border border-slate-100 p-3" data-testid="migration-history-row">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-800">{seasons.find((s) => s.id === m.season_id)?.name || m.season_id}</div>
                    <div className="text-xs text-slate-500">{fmtDate(m.executed_at)} · بواسطة {m.executed_by_name} · {m.changes.length} تغيير</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${m.status === 'applied' ? 'bg-teal-50 text-teal-700' : 'bg-slate-100 text-slate-500'}`}>{m.status === 'applied' ? 'مُنفَّذ' : 'أُلغي (تراجع)'}</span>
                    {m.status === 'applied' && <button onClick={() => setUndoTarget(m)} className="rounded-lg border border-red-200 px-2.5 py-1 text-xs font-semibold text-red-600 hover:bg-red-50" data-testid="migration-undo-btn">تراجع</button>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Modal>

      <Modal open={Boolean(undoTarget)} title="تأكيد التراجع عن الترحيل" onClose={() => setUndoTarget(null)}>
        <p className="text-sm text-slate-600">سيُعاد {undoTarget?.changes?.length || 0} لاعباً إلى فئتهم السابقة (فقط من لم تتغيّر فئته منذ ذلك). متابعة؟</p>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={() => setUndoTarget(null)} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-600">إلغاء</button>
          <button onClick={undo} className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white" data-testid="migration-confirm-undo-btn">تأكيد التراجع</button>
        </div>
      </Modal>
    </div>
  );
}
